import React, { ComponentType, createContext } from "react";
import {AppConfiguration} from "../config/app-configuration";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";
import {AppState} from "../../components/app/app-mobx";
import {MapState} from "../map-state";
import {UiState} from "../ui-state";

import {CompareStore} from "./compare-store-mobx";
import {LayerListStore} from "./layer-list-store-mobx";
import {MapFinderStore} from "./map-finder-store-mobx";
import {PopupStore} from "./popup-store-mobx";
import {TopicSwitcherStore} from "./topic-switcher-store-mobx";
import {SearchStore} from "./search-store-mobx";
import {LayerSelectorStore} from "./layer-selector-store-mobx";
import {PathUtils} from "../../utils/path-utils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {FormatUtils} from "../../utils/format-utils";
import {
    FillSymbolConfiguration,
    PictureMarkerSymbolConfiguration,
    SimpleLineSymbolConfiguration
} from "../config/symbol-configuration";
import EsriGraphic from "@arcgis/core/Graphic";
import EsriPoint from "@arcgis/core/geometry/Point";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import {autorun, runInAction} from "mobx";
import {MapUtils} from "../../utils/map-utils";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Viewpoint from "@arcgis/core/Viewpoint";
import {SearchSourceConfiguration, SearchSourceQueryConfiguration} from "../config/search-configuration";
import {PopupBase} from "../components/popup/popup";

export type StoreType = CompareStore | LayerListStore | LayerSelectorStore | MapFinderStore | PopupStore |
    SearchStore | TopicSwitcherStore;
export type DataStoreType = any;


export interface IAppStore {
    /* add functions when adding abstract functions on AbstractAppStore */
    appConfig: AppConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    appStore: AppState;
    mapStore: MapState;
    uiStore: UiState;

    CompareStore: CompareStore;
    LayerListStore: LayerListStore;
    LayerSelectorStore: LayerSelectorStore;
    MapFinderStore: MapFinderStore;
    PopupStore: PopupStore;
    SearchStore: SearchStore;
    TopicSwitcherStore: TopicSwitcherStore;
}

export interface AbstractAppStoreProps {
    appConfig: AppConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    appStore: AppState;
    mapStore: MapState;
    uiStore: UiState;
}

export abstract class AbstractAppStore implements IAppStore {
    appConfig: AppConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    appStore: AppState;
    mapStore: MapState;
    uiStore: UiState;

    componentsStores: Map<string, StoreType> = new Map<string, StoreType>();
    geoDataStores: Map<string, DataStoreType>; // add data stores here

    focusArgs: number[] = [];

    protected constructor(props: AbstractAppStoreProps) {
        // do not use makeObservable here. Not supported for derived classes
        // use makeObservable in constructor in each derived class
        // make sure you include all properties and functions of this abstract base class!
        // !! add observable, action, computed in derived classes when new functionality is added to this class !!
        this.appConfig = props.appConfig;
        this.mapViewerConfig = props.mapViewerConfig;
        this.appStore = props.appStore;
        this.mapStore = props.mapStore;
        this.uiStore = props.uiStore;
    }

    /* add abstract functions here. Do not forget to add these functions in IAppStore */

    get CompareStore(): CompareStore {
        if(!this.componentsStores.has("compare")){
            this.componentsStores.set(
                "compare",
                new CompareStore()
            );
        }
        return this.componentsStores.get("compare") as CompareStore;
    }
    get LayerListStore(): LayerListStore {
        if(!this.componentsStores.has("layer-list")){
            this.componentsStores.set(
                "layer-list",
                new LayerListStore()
            );
        }
        return this.componentsStores.get("layer-list") as LayerListStore;
    }
    get LayerSelectorStore(): LayerSelectorStore {
        const config = this.mapViewerConfig.layerSelector;
        if(!this.componentsStores.has("layer-selector")) {
            this.componentsStores.set(
                "layer-selector",
                new LayerSelectorStore(this, config)
            );
        }
        return this.componentsStores.get("layer-selector") as LayerSelectorStore;
    }
    get MapFinderStore(): MapFinderStore {
        if(!this.componentsStores.has("map-finder")){
            this.componentsStores.set(
                "map-finder",
                new MapFinderStore()
            );
        }
        return this.componentsStores.get("map-finder") as MapFinderStore;
    }
    get PopupStore(): PopupStore {
        if(!this.componentsStores.has("popup")){
            this.componentsStores.set(
                "popup",
                new PopupStore()
            );
        }
        return this.componentsStores.get("popup") as PopupStore;
    }
    get SearchStore(): SearchStore {
        if(!this.componentsStores.has("search")){
            this.componentsStores.set(
                "search",
                new SearchStore()
            );
        }
        return this.componentsStores.get("search") as SearchStore;
    }
    get TopicSwitcherStore(): TopicSwitcherStore {
        if(!this.componentsStores.has("topic-switcher")){
            this.componentsStores.set(
                "topic-switcher",
                new TopicSwitcherStore()
            );
        }
        return this.componentsStores.get("topic-switcher") as TopicSwitcherStore;
    }

    showObject(layerObjectId: string): void {
        const [layerMetaId, objectId] = layerObjectId.split("|");
        this.PopupStore.setSelected();
        const searchLayer = this.mapStore.map?.allLayers.flatten((subL: __esri.Layer) => {
            return (subL as __esri.MapImageLayer).allSublayers as unknown as __esri.Collection<__esri.Layer>;
        }).find((l: __esri.Layer) => {
            let metaId = (l as any).metauid;
            if (!metaId){
                const url = PathUtils.getLayerPath(l);
                if (typeof url === "string") {
                    metaId = PathUtils.getMetaId(url);
                }
            }
            // selber definierte Gruppen ignorieren (id >= 1000)
            return layerMetaId === metaId  && !(typeof l.id === "number" && l.id >= 1000);
        });
        if (!searchLayer) {
            // eslint-disable-next-line no-console
            console.warn("Kein passenden Layer für URL Parameter "+layerMetaId+" gefunden");
            return;
        }

        const searchLayers: Array<FeatureLayer | __esri.Sublayer | undefined> = [];
        const queryPromises: Array<Promise<__esri.FeatureSet | undefined>> = [];
        let resultFeatures: __esri.Graphic[] = [];
        const query = (searchLayer as FeatureLayer).createQuery();
        if((searchLayer as any).anchorField) {
            if((searchLayer as any).anchorFieldType === "integer"){
                query.where = (searchLayer as any).anchorField + " = " + parseInt(objectId, 10);
            }else{
                query.where = (searchLayer as any).anchorField + " = '" + FormatUtils.encodeForSQL(objectId) + "'";
            }
        } else {
            query.where = "objectid = "+ objectId;
        }
        query.outFields = ["*"];
        searchLayers.push(searchLayer as FeatureLayer);
        searchLayers.forEach((sLayer)=>{
            queryPromises.push(new Promise((resolve) => {
                if(sLayer) {
                    sLayer.queryFeatures(query).then((response: __esri.FeatureSet) => {
                        if (response.features.length > 0) {
                            resultFeatures = resultFeatures.concat(response.features);
                        }
                        resolve(response);
                    });
                }
            }));
        });
        Promise.all(queryPromises).then(() => {
            if (resultFeatures.length === 0) {
                // eslint-disable-next-line no-console
                console.warn("Kein Resultat für URL Parameter gefunden");
                return;
            }
            this.PopupStore.setSelected(objectId);
            if (this.appConfig.search.resultSymbol) {
                let highlightSymbol:
                    FillSymbolConfiguration
                    | PictureMarkerSymbolConfiguration
                    | SimpleLineSymbolConfiguration;
                if (resultFeatures[0].geometry.type === "polygon"){
                    highlightSymbol = this.appConfig.search.resultSymbol.highlight;
                } else if (resultFeatures[0].geometry.type === "polyline"){
                    highlightSymbol = this.appConfig.search.resultSymbol.line;
                } else {
                    highlightSymbol = this.appConfig.search.resultSymbol.pointer;
                }
                this.mapStore?.view?.graphics.removeAll();
                const geometries: __esri.Geometry[] = [];
                resultFeatures.forEach((item) => {
                    this.mapStore?.view?.graphics.push(
                        new EsriGraphic({
                            geometry: item.geometry,
                            symbol: highlightSymbol
                        })
                    );
                    geometries.push(item.geometry);
                });
                this.goToGeometries(geometries);
                if (!this.PopupStore.disabled
                    && (searchLayer as any).popupEnabled && (searchLayer as any).popupTemplate) {
                    let popupPoint: __esri.Point;
                    if (resultFeatures[0].geometry.type === "polygon"){
                        popupPoint = (resultFeatures[0].geometry as __esri.Polygon)?.centroid as EsriPoint;
                    } else if (resultFeatures[0].geometry.type === "polyline"){
                        popupPoint = geometryEngine.nearestCoordinate(
                            resultFeatures[0].geometry as __esri.Geometry,
                            popupPoint=resultFeatures[0].geometry.extent.center).coordinate;
                    } else {
                        popupPoint=resultFeatures[0].geometry as EsriPoint;
                    }
                    runInAction(()=> {
                        this.PopupStore.setFeatures(resultFeatures);
                        this.PopupStore.setMapPoint(popupPoint);
                    });
                }
            }
        });
    }

    goToGeometries(geometries: __esri.Geometry | __esri.Geometry[]): void {
        if(!this.mapStore.view){
            return;
        }
        this.mapStore.view.goTo(MapUtils.calculateMinExtent(geometries)).catch((error) => {
            if (error.name !== "view:goto-interrupted") {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
    }

    processAdditionalParams(searchParams: URLSearchParams): void {
        const {additionalParameter} = this.mapViewerConfig;
        if (!additionalParameter) {
            return;
        }
        const searchParamsLower = new URLSearchParams();
        for (const [name, value] of searchParams) {
            searchParamsLower.append(name.toLowerCase(), value);
        }
        const focus = searchParamsLower.get("focus");
        const marker = searchParamsLower.get("marker");
        const basemap = searchParamsLower.get("basemap");
        const bfs = searchParamsLower.get("bfs");
        const egrid = searchParamsLower.get("egrid");
        const parcel = searchParamsLower.get("parcel");
        const gvlNo = searchParamsLower.get("gvl")? searchParamsLower.get("gvl"): searchParamsLower.get("building");
        const egid = searchParamsLower.get("egid");
        if (focus) {
            const focusArgs = focus.split(/\D/);
            if (focusArgs.length === 3 ) {
                this.focusArgs = [parseInt(focusArgs[0], 10),
                    parseInt(focusArgs[1], 10),
                    parseInt(focusArgs[2], 10)
                ];
                delete this.appConfig.mapView.zoom;
                delete this.appConfig.mapView.viewpoint;
                this.appConfig.mapView.center = new EsriPoint({
                    x: this.focusArgs[0],
                    y: this.focusArgs[1],
                    spatialReference: this.appConfig.mapView.spatialReference
                });
                this.appConfig.mapView.scale = this.focusArgs[2];
                reactiveUtils.when(
                    () => this.mapStore?.view?.ready,
                    () => {
                        if (!this.mapStore.view) {
                            return;
                        }

                        const geometry = new EsriPoint({
                            x: this.focusArgs[0],
                            y: this.focusArgs[1],
                            spatialReference: this.appConfig.mapView.spatialReference
                        });
                        this.mapStore.view.scale = this.focusArgs[2];
                        const target = new Viewpoint({
                            scale: this.focusArgs[2],
                            targetGeometry: geometry
                        });
                        this.mapStore.view.goTo(target).catch((error) => {
                            if (error.name !== "view:goto-interrupted") {
                                // eslint-disable-next-line no-console
                                console.error(error);
                            }
                        });
                        if (marker !== null) {
                            let symbol;
                            if (additionalParameter.marker
                                && additionalParameter.marker[marker.toLowerCase()]) {
                                symbol = additionalParameter.marker[marker.toLowerCase()];
                            } else {
                                symbol = this.appConfig.search.resultSymbol?.pointer;
                            }
                            if (symbol) {
                                this.mapStore.view.graphics.push(
                                    new EsriGraphic({
                                        geometry,
                                        symbol
                                    })
                                );
                            }
                        }
                    },
                    {initial: true, once: true}
                );
            }
        } else if (bfs || egrid || parcel || gvlNo || egid) {
            // TODO: Code überarbeiten und mit Search abstimmen
            const sourceConfig = this.appConfig.search.sources.find(
                (s: SearchSourceConfiguration) =>
                    s.name === (bfs? "Gemeinden, Orte, Flurnamen" : gvlNo?
                        "Gebäudeversicherungsnummern GVL" : parcel?
                            "Grundstücke" : egid?
                                "Eidg. Gebäudeidentifikator EGID": "Eidg. Grundstückidentifikator EGRID"));
            autorun((r: any) => {
                const popup = this.uiStore.widgets.get("popup") as PopupBase;
                r.dispose(); // stop autorun
                if (sourceConfig) {
                    let searchLayer: FeatureLayer | __esri.Sublayer | undefined;
                    let queryConfig: SearchSourceQueryConfiguration | undefined;
                    if (bfs && sourceConfig.queries) {
                        queryConfig = sourceConfig.queries.find((q) => q.id === "GRZGEMLU");
                    }
                    if (!queryConfig && sourceConfig.query) {
                        queryConfig = sourceConfig.query;
                    }
                    const searchLayerPromises: Array<Promise<FeatureLayer | __esri.Sublayer | undefined>> = [];
                    if (sourceConfig.query?.id === "AVLSPRZX" && popup && popup.avlsLayer) {
                        searchLayerPromises.push(new Promise((resolve) => {
                            popup.avlsLayer?.load().then(()=> {
                                if (popup.avlsLayer?.hasOwnProperty("allSublayers")) {
                                    searchLayer = (popup.avlsLayer as __esri.MapImageLayer).allSublayers.getItemAt(0);
                                } else {
                                    searchLayer = popup.avlsLayer as FeatureLayer;
                                }
                                resolve(searchLayer);
                            });
                        }));
                    } else if (queryConfig?.portalItem){
                        const layerConfig = {
                            layerType: "feature",
                            layerId: (queryConfig.layerId) ? queryConfig.layerId : undefined,
                            portalItem: queryConfig.portalItem,
                            opacity: 1,
                            listMode: "hide",
                            legendEnabled: false
                        };
                        searchLayerPromises.push(new Promise((resolve) => {
                            searchLayer = new FeatureLayer(layerConfig as __esri.FeatureLayerProperties);
                            resolve(searchLayer);
                        }));
                        if (!bfs && sourceConfig.queries) {
                            sourceConfig.queries.forEach((q)=>{
                                searchLayerPromises.push(new Promise((resolve) => {
                                    if (q.portalItem) {
                                        layerConfig.layerId = (q.layerId) ? q.layerId : undefined;
                                        layerConfig.portalItem = q.portalItem;
                                        searchLayer = new FeatureLayer(layerConfig as __esri.FeatureLayerProperties);
                                        resolve(searchLayer);
                                    }
                                }));
                            });
                        }
                    }
                    Promise.all(searchLayerPromises).then((searchLayers) => {
                        if (searchLayer && queryConfig) {
                            let where;
                            if (parcel) {
                                where = parcel.split(/[^\d.]/).map(
                                    (no) => {
                                        const [gbcode, prznr] = no.split(".");
                                        return prznr? queryConfig?.expression.replace("$feature.gbcode",
                                            ""+parseInt(gbcode, 10)).replace("$feature.prznr", ""+parseInt(prznr, 10))
                                            .replace(/[$`{}]/g,"") : null;
                                    }
                                ).join(" OR ");
                            } else if (egrid) {
                                where = egrid.split(/\DCH/i).map(
                                    (no) => queryConfig?.expression.replace("$feature.egrid",
                                        "CH" + parseInt(no.replace(/\D/g, ""), 10))
                                        .replace(/[$`{}]/g,"")
                                ).join(" OR ");
                            } else if (bfs) {
                                where = bfs.split(/\D/i).map(
                                    (no) => queryConfig?.expression.replace("$feature.bfsnr",
                                        ""+parseInt(no, 10))
                                        .replace(/[$`{}]/g,"")
                                ).join(" OR ");
                            } else if (egid) {
                                where = egid.split(/\D/i).map(
                                    (no) => queryConfig?.expression.replace("$feature.egid",
                                        ""+parseInt(no, 10))
                                        .replace(/[$`{}]/g,"")
                                ).join(" OR ");
                            } else if (gvlNo) {
                                where = gvlNo.split(/[^\d.a-z]/i).map(
                                    (no) => {
                                        const [gbcode, gvnr] = no.split(".");
                                        return gvnr? queryConfig?.expression.replace("$feature.gbcode",
                                            ""+parseInt(gbcode, 10)).replace("$feature.gvnr", gvnr)
                                            .replace(/[$`{}]/g,"") : null;
                                    }
                                ).join(" OR ");
                            }
                            if (typeof where === "string") {
                                const queryPromises: Array<Promise<__esri.FeatureSet | undefined>> = [];
                                let resultFeatures: __esri.Graphic[] = [];
                                const query = searchLayer.createQuery();
                                query.where = where;
                                searchLayers.forEach((sLayer)=>{
                                    queryPromises.push(new Promise((resolve) => {
                                        if(sLayer) {
                                            sLayer.queryFeatures(query).then((response) => {
                                                if (response.features.length > 0) {
                                                    resultFeatures = resultFeatures.concat(response.features);
                                                }
                                                resolve(response);
                                            });
                                        }
                                    }));
                                });
                                Promise.all(queryPromises).then(() => {
                                    if (resultFeatures.length === 0) {
                                        // eslint-disable-next-line no-console
                                        console.warn("Kein Resultat für URL Parameter gefunden");
                                        return;
                                    }
                                    if (this.appConfig.search.resultSymbol) {
                                        let highlightSymbol: FillSymbolConfiguration | PictureMarkerSymbolConfiguration;
                                        if (resultFeatures[0].geometry.type === "polygon"){
                                            highlightSymbol = this.appConfig.search.resultSymbol.highlight;
                                        } else {
                                            highlightSymbol = this.appConfig.search.resultSymbol.pointer;
                                        }
                                        const geometries: __esri.Geometry[] = [];
                                        resultFeatures.forEach((item) => {
                                            this.mapStore?.view?.graphics.push(
                                                new EsriGraphic({
                                                    geometry: item.geometry,
                                                    symbol: highlightSymbol
                                                })
                                            );
                                            geometries.push(item.geometry);
                                        });
                                        this.goToGeometries(geometries);
                                    }
                                });
                            }
                        }
                    });
                }
            });
        }
        if (basemap) {
            let basemapTag: string;
            if (additionalParameter?.basemap && additionalParameter.basemap[basemap]) {
                basemapTag = additionalParameter.basemap[basemap];
            } else {
                basemapTag = "MV6:" + basemap.replace(/mv6:/i, "").toLowerCase();
            }
            autorun((r: any) => {
                if (!this.mapStore.view || !this.mapStore.map) {
                    return;
                }
                r.dispose(); // stop autorun
                this.mapStore.setBasemap(basemapTag);
            });
        }
    }

}


/* Store helpers */
type AppStoreContextType = IAppStore | null;
const AppStoreContext = createContext<AppStoreContextType>(null);

interface AppStoreProviderProps {
    children: React.ReactNode;
    store: IAppStore;
}

export const AppStoreProvider = ({ children, store }: AppStoreProviderProps) => {
    return (
        <AppStoreContext.Provider value={store}>{children}</AppStoreContext.Provider>
    );
};

/* Hook to use store in any functional component */
export const useStore = () => {
    return React.useContext(AppStoreContext);
};

/* Higher order component (HOC) to inject store to any functional or class component */
// eslint-disable-next-line react/display-name
export const withStore = (Component: ComponentType<any>) => (props: any) => {
    return <Component {...props} store={useStore()} />;
};
